import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { TIDS } from "@/lib/testIds";
import { Save } from "lucide-react";

const PAYMENT_METHODS = ["ach", "check", "wire", "zelle", "other"];
const UNASSIGNED = "__unassigned__";

/**
 * Edit dialog for a referral partner (admin-only).
 * Uses PATCH /api/admin/partners/:id — sends only changed fields.
 */
export function EditPartnerDialog({ partner, open, onOpenChange, onSaved }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [company, setCompany] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [employeeId, setEmployeeId] = useState(UNASSIGNED);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && partner) {
      setFullName(partner.full_name || "");
      setEmail(partner.email || "");
      setMobile(partner.mobile || "");
      setCompany(partner.company_name || "");
      setPaymentMethod(partner.payment_method || "");
      setEmployeeId(partner.recruited_by_employee_id || UNASSIGNED);
      // fetch employees for the dropdown once per open
      apiClient
        .get("/admin/employees")
        .then((res) => setEmployees(res.data))
        .catch(() => {});
    }
  }, [open, partner]);

  const save = async (e) => {
    e?.preventDefault();
    if (!partner?.id) return;
    const payload = {};
    if (fullName.trim() !== (partner.full_name || "")) payload.full_name = fullName.trim();
    if (email.trim().toLowerCase() !== (partner.email || "").toLowerCase()) payload.email = email.trim();
    if ((mobile || "") !== (partner.mobile || "")) payload.mobile = mobile.trim();
    if ((company || "") !== (partner.company_name || "")) payload.company_name = company.trim();
    if ((paymentMethod || "") !== (partner.payment_method || "")) payload.payment_method = paymentMethod;
    const currentEmp = partner.recruited_by_employee_id || UNASSIGNED;
    if (employeeId !== currentEmp) {
      payload.recruited_by_employee_id = employeeId === UNASSIGNED ? "" : employeeId;
    }

    if (Object.keys(payload).length === 0) {
      toast.message("Nothing to update");
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.patch(`/admin/partners/${partner.id}`, payload);
      toast.success("Partner updated");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-partner-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-medium tracking-tight">Edit partner</DialogTitle>
          <DialogDescription>
            Update {partner?.full_name || "this partner"}&apos;s contact info, payment method, or employee attribution.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="pt-fn">Full name</Label>
            <Input id="pt-fn" data-testid={TIDS.editPartnerName} value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="pt-em">Email</Label>
              <Input id="pt-em" type="email" data-testid={TIDS.editPartnerEmail} value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-mo">Mobile</Label>
              <Input id="pt-mo" type="tel" data-testid={TIDS.editPartnerMobile} value={mobile} onChange={(e) => setMobile(e.target.value)} required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pt-co">Company <span className="text-slate-400 font-normal text-xs">(optional)</span></Label>
            <Input id="pt-co" data-testid={TIDS.editPartnerCompany} value={company} onChange={(e) => setCompany(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="pt-pm">Payment method</Label>
              <Select value={paymentMethod || "none"} onValueChange={(v) => setPaymentMethod(v === "none" ? "" : v)}>
                <SelectTrigger id="pt-pm" data-testid={TIDS.editPartnerPayment}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— not set —</SelectItem>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-em2">Attributed to</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger id="pt-em2" data-testid={TIDS.editPartnerEmployee}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>— unassigned —</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2 pt-1">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid={TIDS.editPartnerSubmit}
              className="rounded-full bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
